import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const DropdownContainer = styled.div<{
    x: number;
    y: number;
    $visible: boolean;
}>`
    position: fixed;
    left: ${(props) => props.x}px;
    top: ${(props) => props.y}px;
    padding: 6px;
    height: auto;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    z-index: 100;
    visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
`;
const Dropdown: FC<{
    buttonRef: HTMLElement;
    anchor: Anchor;
    alignment: Alignment;
    children?: React.ReactNode;
}> = ({ buttonRef: el, anchor, alignment, children }) => {
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [x, setX] = useState(0);
    const [y, setY] = useState(0);

    const getPosition = (anchor: Anchor, alignment: Alignment) => {
        if (!dropdownRef.current) return [0, 0];

        const dropdownWidth = dropdownRef.current.getBoundingClientRect().width;
        const dropdownHeight = dropdownRef.current.getBoundingClientRect().height;

        const buttonX = el.getBoundingClientRect().x;
        const buttonY = el.getBoundingClientRect().y;
        const buttonWidth = el.getBoundingClientRect().width;
        const buttonHeight = el.getBoundingClientRect().height;

        let dropdownPositionX = buttonX;
        let dropdownPositionY = buttonY;

        switch (anchor) {
            case 'top':
                dropdownPositionY = buttonY - dropdownHeight;

                if (alignment === 'right') {
                    dropdownPositionX = buttonX + buttonWidth - dropdownWidth;
                }
                break;
            case 'bottom':
                dropdownPositionY = buttonY + buttonHeight;

                if (alignment === 'right') {
                    dropdownPositionX = buttonX + buttonWidth - dropdownWidth;
                }
                break;
            case 'left':
                dropdownPositionX = buttonX - dropdownWidth;

                if (alignment === 'bottom') {
                    dropdownPositionY = buttonY + buttonHeight - dropdownHeight;
                }
                break;
            case 'right':
                dropdownPositionX = buttonX + buttonWidth;

                if (alignment === 'bottom') {
                    dropdownPositionY = buttonY + buttonHeight - dropdownHeight;
                }
                break;
        }

        return [dropdownPositionX, dropdownPositionY];
    };

    useLayoutEffect(() => {
        if (!dropdownRef.current) return;

        const dropdownWidth = dropdownRef.current.getBoundingClientRect().width;
        const dropdownHeight = dropdownRef.current.getBoundingClientRect().height;

        let currentAnchor = anchor;
        let currentAlignment = alignment;

        let [dropdownPositionX, dropdownPositionY] = getPosition(currentAnchor, currentAlignment);

        if (dropdownPositionX + dropdownWidth > window.innerWidth) {
            if (currentAnchor === 'right') {
                [dropdownPositionX, dropdownPositionY] = getPosition('left', currentAlignment);
                currentAnchor = 'left';
            } else {
                [dropdownPositionX, dropdownPositionY] = getPosition(currentAnchor, 'right');
                currentAlignment = 'right';
            }
        }

        if (dropdownPositionX < 0) {
            if (currentAnchor === 'left') {
                [dropdownPositionX, dropdownPositionY] = getPosition('right', currentAlignment);
                currentAnchor = 'right';
            } else {
                [dropdownPositionX, dropdownPositionY] = getPosition(currentAnchor, 'left');
                currentAlignment = 'left';
            }
        }

        if (dropdownPositionY + dropdownHeight > window.innerHeight) {
            if (currentAnchor === 'bottom') {
                [dropdownPositionX, dropdownPositionY] = getPosition('top', currentAlignment);
                currentAnchor = 'top';
            } else {
                [dropdownPositionX, dropdownPositionY] = getPosition(currentAnchor, 'bottom');
                currentAlignment = 'bottom';
            }
        }

        if (dropdownPositionY < 0) {
            if (currentAnchor === 'top') {
                [dropdownPositionX, dropdownPositionY] = getPosition('bottom', currentAlignment);
                currentAnchor = 'bottom';
            } else {
                [dropdownPositionX, dropdownPositionY] = getPosition(currentAnchor, 'top');
                currentAlignment = 'top';
            }
        }

        setX(dropdownPositionX);
        setY(dropdownPositionY);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [el, anchor, alignment, dropdownRef.current]);

    return createPortal(
        <DropdownContainer onClick={(e) => e.nativeEvent.stopPropagation()} ref={dropdownRef} x={x} y={y} $visible={dropdownRef.current !== null}>
            {children}
        </DropdownContainer>,
        document.body
    );
};

type Anchor = 'left' | 'top' | 'bottom' | 'right';

type Alignment = 'left' | 'top' | 'bottom' | 'right';

const activeDropdownCloseRef: { current: (() => void) | null } = {
    current: null
};

export const useDropdown = () => {
    const [isOpened, setIsOpened] = useState(false);
    const [anchor, setAnchor] = useState<Anchor>('bottom');
    const [alignment, setAlignment] = useState<Alignment>('left');

    const elementRef = useRef<HTMLElement | null>(null);

    const close = useCallback(() => {
        setIsOpened(false);

        if (activeDropdownCloseRef.current === close) {
            activeDropdownCloseRef.current = null;
        }
    }, []);

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            // If clicked element contain elementRef.current then do nothing
            if (elementRef.current && elementRef.current.contains(e.target as HTMLElement)) {
                e.stopPropagation();
                return;
            }

            close();
        };

        document.addEventListener('click', onClick);
        return () => {
            document.removeEventListener('click', onClick);

            if (activeDropdownCloseRef.current === close) {
                activeDropdownCloseRef.current = null;
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [close]);

    const CustomDropdown: FC<{ children?: React.ReactNode }> = ({ children }) => {
        if (!elementRef.current) return null;

        return (
            <Dropdown buttonRef={elementRef.current} anchor={anchor} alignment={alignment}>
                {children}
            </Dropdown>
        );
    };

    const open = useCallback(
        (el: HTMLElement, anchor: Anchor = 'bottom', alignment: Alignment = 'left') => {
            if (activeDropdownCloseRef.current && activeDropdownCloseRef.current !== close) {
                activeDropdownCloseRef.current();
            }

            activeDropdownCloseRef.current = close;

        setIsOpened(true);
        setAlignment(alignment);
        setAnchor(anchor);
        elementRef.current = el;
        },
        [close]
    );

    const result = useMemo(
        () => [open, close, isOpened, CustomDropdown] as const,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [anchor, alignment, close, isOpened, open]
    );

    return result;
};
